import React from 'react'
import { useQuery, UseQueryResult } from 'react-query'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import {
  Add as AddIcon,
  BlurLinear as BlurLinearIcon,
  LocalGroceryStore as LocalGroceryStoreIcon,
} from '@material-ui/icons'

import {
  useQueryPaginator,
  BarcodeField,
  BooleanField,
  DateTimeField,
  DetailTable,
  NumberField,
  PaginatedTable,
  RelatedLinkedField,
  StatusField,
  ValueField,
} from 'src/components/Table'

import { skuit } from 'src/services/skuit'
import { Product, Sku } from 'src/api/models'
import { StatsTable } from 'src/components/Stats/StatsTable'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Button = styled(MuiButton)(spacing)

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface DetailsProps {
  sku: UseQueryResult<Sku>
}

const Details = (props: DetailsProps) => {
  const { sku } = props
  return (
    <Card mb={6}>
      <CardContent>
        <Centered>
          <BlurLinearIcon />
          <Typography variant="h6" gutterBottom>
            {sku.data?.sku}
          </Typography>
          <DetailTable
            obj={sku}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'sku', label: 'SKU', renderer: ValueField },
              { id: 'productName', label: 'Product Name', renderer: ValueField },
              { id: 'numAvailable', label: 'Number of Available Units', renderer: NumberField },
              { id: 'numReserved', label: 'Number of Units in Orders', renderer: NumberField },
              {
                id: 'numAwaitingShipment',
                label: 'Number of Units Awaiting Shipment',
                renderer: NumberField,
              },
              { id: 'isActive', label: 'Is Active', renderer: BooleanField },
            ]}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface ProductDetailsProps {
  product: UseQueryResult<Product>
}

const ProductDetails = (props: ProductDetailsProps) => {
  const { product } = props
  return (
    <Card mb={6}>
      <CardContent>
        <Centered>
          <LocalGroceryStoreIcon />
          <Typography variant="h6" gutterBottom>
            {product && product.data?.name}
          </Typography>
          <DetailTable
            obj={product}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'name', label: 'Name', renderer: ValueField },
              { id: 'primaryBarcode', label: 'Primary Barcode', renderer: BarcodeField },
            ]}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const SkuPage = () => {
  const { guid } = useParams<PathParams>()
  const sku = useQuery<Sku>(['sku', guid], () => skuit.client.skus.retrieve(guid))
  const { data: stats } = useQuery(['sku-stats', guid], () => skuit.client.skus.stats(guid))

  const skuByFacilityPaginator = useQueryPaginator(
    'sku-by-facility',
    () => skuit.client.skus.byFacility(guid),
    {
      params: { page_size: 5, skus: guid },
    }
  )
  const skuASNPaginator = useQueryPaginator('sku-asn', skuit.client.asn.units.list, {
    params: { page_size: 5, skus: guid },
  })
  const skuFreightOrderPaginator = useQueryPaginator(
    'sku-freight-order',
    skuit.client.freight.orders.units.list,
    {
      params: { page_size: 5, skus: guid },
    }
  )
  const skuFreightShipmentPaginator = useQueryPaginator(
    'sku-freight-shipment',
    skuit.client.freight.shipments.units.list,
    {
      params: { page_size: 5, skus: guid },
    }
  )
  const skuParcelOrderPaginator = useQueryPaginator(
    'sku-parcel-order',
    skuit.client.parcel.orders.units.list,
    {
      params: { page_size: 5, skus: guid },
    }
  )
  const skuParcelShipmentPaginator = useQueryPaginator(
    'sku-parcel-shipment',
    skuit.client.parcel.shipments.units.list,
    {
      params: { page_size: 5, skus: guid },
    }
  )

  return (
    <>
      <Helmet title="SKU" />

      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            SKU
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/inventory/skus">
              SKUs
            </Link>
            <Typography>{sku && sku.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button variant="contained" color="primary" onClick={() => alert('Coming soon!')}>
              <AddIcon />
              New SKU
            </Button>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={6}>
              <Grid item xs={12} lg={6}>
                {sku && <Details sku={sku} />}
              </Grid>
              <Grid item xs={12} lg={6}>
                {sku.data?.product && (
                  <ProductDetails
                    product={{ ...sku, data: sku.data.product } as UseQueryResult<Product>}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {stats && (
            <StatsTable
              titles={['Total Ordered', 'Total Received', 'Total Shipped']}
              values={[stats.total_ordered, stats.total_received, stats.total_shipped]}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <PaginatedTable
            title="Stored by Facility"
            fields={[
              {
                id: 'facility',
                label: 'Facility',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'numAvailable', label: 'Number of Units Available', renderer: NumberField },
              { id: 'numReserved', label: 'Number of Units in Orders', renderer: NumberField },
              { id: 'numUnits', label: 'Total Number of Units', renderer: NumberField },
            ]}
            paginator={skuByFacilityPaginator}
          />
        </Grid>

        <Grid item xs={12}>
          <PaginatedTable
            title="SKUs Received"
            fields={[
              {
                id: 'asn',
                label: 'Advanced Shipping Notice',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'asn.receivedOn', label: 'Received On', renderer: DateTimeField },
              { id: 'numUnits', label: 'Number of Units', renderer: NumberField },
            ]}
            paginator={skuASNPaginator}
          />
        </Grid>

        <Grid item xs={12}>
          <PaginatedTable
            title="SKUs Ordered by Freight"
            fields={[
              {
                id: 'order',
                label: 'Order',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'order.placedOn',
                label: 'Order Placed On',
                renderer: DateTimeField,
              },
              { id: 'order.status', label: 'Order Status', renderer: StatusField },
              { id: 'numUnits', label: 'Number of Units', renderer: NumberField },
            ]}
            paginator={skuFreightOrderPaginator}
          />
        </Grid>

        <Grid item xs={12}>
          <PaginatedTable
            title="SKUs Shipped by Freight"
            fields={[
              {
                id: 'shipment',
                label: 'Shipment',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'shipment.order',
                label: 'Order',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'shipment.createdOn', label: 'Shipped On', renderer: DateTimeField },
              { id: 'numUnits', label: 'Number of Units', renderer: NumberField },
            ]}
            paginator={skuFreightShipmentPaginator}
          />
        </Grid>

        <Grid item xs={12}>
          <PaginatedTable
            title="SKUs Ordered by Parcel"
            fields={[
              {
                id: 'order',
                label: 'Order',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'order.placedOn',
                label: 'Order Placed On',
                renderer: DateTimeField,
              },
              { id: 'order.status', label: 'Order Status', renderer: StatusField },
              { id: 'numUnits', label: 'Number of Units', renderer: NumberField },
            ]}
            paginator={skuParcelOrderPaginator}
          />
        </Grid>

        <Grid item xs={12}>
          <PaginatedTable
            title="SKUs Shipped by Parcel"
            fields={[
              {
                id: 'shipment',
                label: 'Shipment',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'shipment.order',
                label: 'Order',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'shipment.createdOn', label: 'Shipped On', renderer: DateTimeField },
              { id: 'numUnits', label: 'Number of Units', renderer: NumberField },
            ]}
            paginator={skuParcelShipmentPaginator}
          />
        </Grid>
      </Grid>
    </>
  )
}
